import { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { toast } from "sonner";
import { AlertTriangle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { api, errMsg } from "@/lib/api";
import { EsitoBadge, StatoBadge } from "@/components/Badges";
import { TheoryEditor } from "@/components/admin/TheoryEditor";
import { ResolvePanel } from "@/components/admin/ResolvePanel";

function Problems({ list, testid }) {
  if (!list?.length) return null;
  return (
    <ul data-testid={testid} className="space-y-1 rounded-xl border border-amber-400/40 bg-amber-400/5 p-3 text-xs text-amber-200">
      {list.map((p) => <li key={p} className="flex gap-1.5"><AlertTriangle size={13} className="shrink-0" /> {p}</li>)}
    </ul>
  );
}

function DossierForm({ d, onSaved }) {
  const [f, setF] = useState({ ...d, correlati: (d.correlati || []).join(", ") });
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const save = async () => {
    try {
      await api.put(`/admin/dossiers/${d.id}`, { titolo: f.titolo, intro: f.intro, categoria: f.categoria, criterio_risoluzione: f.criterio_risoluzione,
        correlati: f.correlati.split(",").map((s) => s.trim()).filter(Boolean) });
      toast.success("Dossier salvato");
      onSaved();
    } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="luma-card space-y-3">
      <label className="luma-label">Domanda (con esiti alternativi, non sì/no)</label>
      <Input value={f.titolo} onChange={set("titolo")} data-testid="dossier-title-input" />
      <label className="luma-label">Categoria</label>
      <Input value={f.categoria} onChange={set("categoria")} data-testid="dossier-category-input" />
      <label className="luma-label">Introduzione breve (facoltativa: usata nelle anteprime e in testa al dossier)</label>
      <Textarea value={f.intro} onChange={set("intro")} rows={3} data-testid="dossier-intro-input" />
      <label className="luma-label">Criterio di risoluzione</label>
      <Textarea value={f.criterio_risoluzione} onChange={set("criterio_risoluzione")} rows={2} data-testid="dossier-criteria-input" />
      <label className="luma-label">Misteri correlati curati (ID separati da virgola, es. M002, M016)</label>
      <Input value={f.correlati} onChange={set("correlati")} data-testid="dossier-related-input" />
      <Button onClick={save} className="rounded-full" data-testid="dossier-save-button">Salva dossier</Button>
    </div>
  );
}

function CandidateSources({ id, sources }) {
  const list = sources.filter((s) => s.dossier_collegati_originali?.includes(id));
  if (!list.length) return null;
  return (
    <section className="luma-card space-y-2 text-sm" data-testid="candidate-sources">
      <h2 className="font-semibold">Fonti candidate citate per questo dossier ({list.length})</h2>
      <p className="text-xs text-amber-300">Collegamento originale NON verificato (alcuni abbinamenti del file sono incoerenti): controlla link, pertinenza e provenienza, poi verifica la fonte nella scheda Fonti e collegala al singolo esito.</p>
      {list.map((s) => (
        <p key={s.id}><span className="font-mono text-xs">{s.id}</span> · <b>{s.autore}</b> · {s.piattaforma} — {s.titolo} <i className="text-xs text-muted-foreground">[{s.stato}{s.flag_italiano ? ", creator italiano" : ""}]</i>{" "}
          <a href={s.url} target="_blank" rel="noopener noreferrer nofollow" className="break-all text-xs text-primary">apri</a></p>
      ))}
    </section>
  );
}

export default function AdminDossier() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [sources, setSources] = useState([]);
  const load = useCallback(() => api.get(`/admin/dossiers/${id}`).then((r) => setData(r.data)).catch((e) => toast.error(errMsg(e))), [id]);
  useEffect(() => { load(); api.get("/admin/sources").then((r) => setSources(r.data)); }, [load]);
  const status = async (s) => {
    try { await api.post(`/admin/dossiers/${id}/status/${s}`); toast.success(`Dossier: ${s}`); load(); } catch (e) { toast.error(errMsg(e)); }
  };
  const addTheory = async () => {
    try { await api.post(`/admin/dossiers/${id}/theories`, { titolo: "Nuovo esito da completare" }); load(); } catch (e) { toast.error(errMsg(e)); }
  };
  if (!data) return <p className="py-20 text-center text-muted-foreground">Caricamento…</p>;
  const d = data.dossier;
  return (
    <div className="space-y-8">
      <div className="space-y-2">
        <Link to="/admin" className="text-sm text-primary">← Dashboard</Link>
        <div className="flex flex-wrap items-center gap-2"><span className="font-mono text-xs">{d.id}</span><StatoBadge stato={d.stato} /><EsitoBadge esito={d.esito} />
          {d.seed_origin && <span className="text-xs text-muted-foreground">contenuto iniziale</span>}</div>
        <h1 className="text-3xl font-bold">{d.titolo}</h1>
        <div className="flex flex-wrap gap-2">
          {d.stato !== "pubblicato" && <Button onClick={() => status("pubblicato")} className="rounded-full" data-testid="dossier-publish-button">Pubblica</Button>}
          {d.stato !== "bozza" && <Button variant="outline" onClick={() => status("bozza")} className="rounded-full" data-testid="dossier-unpublish-button">Riporta in bozza</Button>}
          {d.stato !== "archiviato" && <Button variant="ghost" onClick={() => status("archiviato")} className="rounded-full" data-testid="dossier-archive-button">Archivia</Button>}
          <Link to={`/dossier/${d.id}`}><Button variant="ghost" className="rounded-full" data-testid="dossier-preview-link">Anteprima</Button></Link>
        </div>
      </div>
      <DossierForm key={d.updated_at} d={d} onSaved={load} />
      <CandidateSources id={d.id} sources={sources} />
      <section className="space-y-4">
        <h2 className="text-lg font-semibold">Esiti / teorie ({data.teorie.length}) · {data.voti_totali} pronostici</h2>
        <p className="text-xs text-muted-foreground">Un solo elenco di esiti: ogni esito può avere più provenienze (mondo, Lumacofono, community). Le interpretazioni senza fonte verificata compaiono pubblicamente come «Ipotesi da documentare»: spiegazioni e fonti si possono aggiungere in qualsiasi momento.</p>
        {data.teorie.map((t) => <TheoryEditor key={`${t.id}-${t.updated_at}`} t={t} all={data.teorie} sources={sources} onSaved={load} Problems={Problems} />)}
        <Button variant="outline" onClick={addTheory} className="rounded-full" data-testid="add-theory-button">Aggiungi esito</Button>
      </section>
      <ResolvePanel data={data} onChange={load} />
    </div>
  );
}
